import { React, css, styled, useTheme } from '../lib/imports';
import { useSelector, useDispatch } from 'react-redux';
import { useCdnUrl, getAssetSet, useCdnImage } from '../app/context/config';
import {
  Carousel,
  CircleText,
  ColorProvider,
  Spacing,
  Heading,
  Text,
  HeatStreakGraph,
  Col,
  Grid,
  Image,
} from '../components';
import UpdateUserName from './UpdateUserName';
import { NavLink } from 'react-router-dom';
import { setHeatStreakPanelToClosed } from '../ui/actions';

const assetSet = (filename) => getAssetSet(useCdnImage, filename);

const containerBaseStyles = ({ theme }) => css`
  background-color: ${theme.colors.black};
  width: 100%;
  padding-top: ${theme.spacings.kilo};
  border-top: 1px solid #444444;
`;

const Container = styled('div')(containerBaseStyles);

const subContainerBaseStyles = ({ theme }) => css`
  background: #2c2c2c;
  border-radius: 11px;
  padding: ${theme.spacings.giga};
  margin-bottom: ${theme.spacings.mega};
`;

const SubContainer = styled('div')(subContainerBaseStyles);

const dividerBaseStyles = ({ theme }) => css`
  border-bottom: 1px solid #444444;
  margin-left: -${theme.spacings.giga};
  margin-right: -${theme.spacings.giga};
`;

const Divider = styled('div')(dividerBaseStyles);

const statusBadgeBaseStyles = (props) => css`
  border-radius: 10.5px;
  border: 0 solid #666;
  background: ${props.color};
  padding: 2px 7.5px;
  text-align: center;
  display: inline-block;
  white-space: nowrap;
  width: 100%;
`;

const StatusBadge = styled(Text)(statusBadgeBaseStyles);

const streakDatesBaseStyles = () => css`
  display: flex;
  justify-content: space-between;
`;

const StreakDates = styled('div')(streakDatesBaseStyles);

const heatStreakItemBaseStyles = ({ theme }) => css`
  text-align: center;
  > div {
    display: inline-block;
  }
`;

const HeatStreakItem = styled('div')(heatStreakItemBaseStyles);

const winStreakBaseStyles = () => css`
  display: flex;
`;

const WinStreak = styled('div')(winStreakBaseStyles);

const WinStreakGraph = styled('div')();

const winStreakTextBaseStyles = ({ theme }) => css`
  margin-left: ${theme.spacings.mega};
`;

const WinStreakText = styled('div')(winStreakTextBaseStyles);

const styledHeadingBaseStyles = ({ theme }) => css`
  line-height: 20px;
  margin-bottom: ${theme.spacings.byte};
`;

const StyledHeading = styled(Heading)(styledHeadingBaseStyles);

const styledTextBaseStyles = () => css`
  line-height: 18px;
`;

const StyledText = styled(Text)(styledTextBaseStyles);

const MiniBannerContainer = styled.div`
  display: flex;
  padding: ${({ theme }) => `${theme.spacings.giga} ${theme.spacings.kilo}`};
  margin: 0 auto;
  background: ${({ theme }) => theme.colors.darkBg} no-repeat top / 100% 266px url(${assetSet('miniBannerBackground')});
  color: ${({ theme }) => theme.colors.white};
`;

const MiniBannerStatus = styled.div`
  display: flex;
  align-items: center;
  margin-top: 5px;
  line-height: 8px;
`;

const MiniBannerTitle = styled(Heading)`
  font-size: ${({ theme }) => theme.typography.text.peta.fontSize};
  line-height: 20px;
  width: 166px;
`;

const MiniBannerDate = styled(Text)`
  font-size: ${({ theme }) => theme.typography.subHeadings.byte.fontSize};
  margin: 0px;
`;

const MiniBannerBadge = styled.span`
  background-color: ${(props) => props.theme.colors[props.color]};
  border-radius: ${({ theme }) => theme.spacings.kilo};
  text-transform: uppercase;
  font-size: ${({ theme }) => theme.typography.text.bit.fontSize};
  font-weight: 900;
  margin-left: 5px;
  padding: ${({ theme }) => `${theme.spacings.bit} ${theme.spacings.kilo}`};
`;

const MiniBannerText = styled(Text)`
  font-size: ${({ theme }) => theme.typography.text.kilo.fontSize};
  line-height: ${({ theme }) => theme.typography.text.bit.lineHeight};
  margin-top: 7px;
  margin-bottom: 0px;
`;

// return width: 50% if Boom apply challenges/dates format in description
const MiniBannerImage = styled(Image)`
  flex: 1;
  width: 45%;
`;

const MiniBannerLink = styled(NavLink)`
  color: ${({ theme }) => theme.colors.white};
  font-size: ${({ theme }) => theme.typography.text.byte.fontSize};
`;

const MiniBannerInformationContainer = styled.div`
  flex: 1;
  padding-right: 9px;
`;

// month mappings from English to French since boom only sends challenge dates in English format
function convertToFrenchDateRange(dateRange) {
  const monthMapping = {
    Jan: 'janv',
    Feb: 'févr',
    Mar: 'mars',
    Apr: 'avr',
    May: 'mai',
    Jun: 'juin',
    Jul: 'juil',
    Aug: 'août',
    Sep: 'sept',
    Oct: 'oct',
    Nov: 'nov',
    Dec: 'déc',
  };

  const regex = /([A-Za-z]{3})\s*(\d+)\s*-\s*(?:[A-Za-z]{3})?\s*(\d+)/;

  const matches = dateRange.match(regex);
  if (!matches) {
    return dateRange;
  }

  const [, month, day1, day2] = matches;

  const frenchMonth = monthMapping[month];

  return `${day1} ${frenchMonth} - ${day2} ${frenchMonth}`;
}

const HeatStreakPanel = () => {
  const theme = useTheme();
  const cdnUrl = useCdnUrl();
  const dispatch = useDispatch();
  const { config, strings, leaderboard, heatStreak } = useSelector((state) => state);
  const { language } = config;
  const { percentage: heatStreakPercentage, streak } = heatStreak;

  const { currentChallenge, userStats, isCurrentlySeason, isCurrentlyPlayoffs } = leaderboard;
  // The panel can be opened before user stats resolve, or after the fetch fails
  // (noContest / auth / 5xx), so every branch below has to tolerate empty stats.
  const { challenges = {}, season = {}, postseason = {} } = userStats;

  const displayedChallenge = challenges[currentChallenge] ?? {};

  const isFrench = language === 'fr';

  const remainingDays = 7 - streak;
  const isStreakComplete = streak && !remainingDays;

  const winStreakTitle = isStreakComplete
    ? strings['heatstreak/panel/winStreak/complete/title']
    : strings['heatstreak/panel/winStreak/title'](streak);
  const winStreakHeader = strings['heatstreak/panel/winStreak/header'];
  const streakBadge = strings['heatstreak/panel/winStreak/badge'];
  const winStreakText = isStreakComplete
    ? strings['heatstreak/panel/winStreak/complete/text']
    : strings['heatstreak/panel/winStreak/text'](remainingDays);
  const challengeStatsTitle = strings['heatstreak/statistic/challengeStats/title'];
  const seasonStatsTitle = strings['heatstreak/statistic/seasonStats/title'];
  const postseasonStatsTitle = strings['heatstreak/statistic/postseasonStats/title'];
  const myChallengesTitle = strings['heatstreak/statistic/mychallenges/title'];
  const statusInProgressText = strings['heatstreak/statistic/status/progress'];
  const statusCompleteText = strings['heatstreak/statistic/status/completed'];
  const challengeDescriptions = strings['challenges/dates'];
  const challengeMonthlyTitle = strings['challenges/monthly/heatstreak/title'];
  const seasonTitle = strings['challenges/season/heatstreak/title'];
  const seasonDates = strings['challenges/season/dates'];
  const playoffDates = strings['challenges/playoffs/dates'];
  const miniBannerLinkText = strings['heatstreak/minibanner/link'];
  const miniBannerLinkRoute = strings['heatstreak/minibanner/route'];
  const seasonBannerTitle = seasonTitle;
  const seasonBannerDate = strings['leaderboard/banner/season/date'];
  const seasonBannerText = strings['leaderboard/banner/season/paragraph'];
  const seasonBannerIMG = `2027_Season_Prize.png`;
  const monthlyBannerTitle = challengeMonthlyTitle;
  let monthlyBannerDate = strings['heatstreak/minibanner/monthly/date'];
  const monthlyBannerText = strings['leaderboard/banner/monthly/paragraph'];
  const monthlyBannerIMG = `2026_monthly_prizes_${language}.png`;
  const playoffsBannerTitle = strings['heatstreak/minibanner/title'];
  const playoffsBannerDate = strings['heatstreak/minibanner/playoffs/date'];
  const playoffsBannerText = strings['heatstreak/minibanner/text'];
  const playoffsBannerIMG = `2026_Playoffs_Prize_${language}.png`;

  const streakMapVariant = {
    0: 'h0',
    1: 'h1',
    2: 'h2',
    3: 'h3',
    4: 'h4',
    5: 'h5',
    6: 'h6',
    7: 'h7',
  };
  const heatStreakVariant = streakMapVariant[streak];
  const streakList = [];
  for (let i = 1; i <= 7; i++) {
    streakList.push({
      primaryTextOrder: isFrench ? 2 : 1,
      secondaryTextOrder: isFrench ? 1 : 2,
      graphVariant: (streak && streak >= i && streakMapVariant[streak]) || 'h0',
    });
  }

  const carouselData = [
    { ...season, title: seasonStatsTitle },
    {
      ...displayedChallenge,
      title: `${challengeStatsTitle} ${displayedChallenge.challengeNumber ?? ''} ${language === 'en' ? 'Stats' : ''}`,
    },
  ];

  if (!isCurrentlySeason) {
    carouselData.length = 0;
    carouselData.push({ ...postseason, title: postseasonStatsTitle });
  }

  // Omit extra challenges sent via Boom - only show challenges during season
  const numberOfChallenges = strings['challenges/dates'].length;
  const challengesWithoutPlayoffs = Object.values(challenges);
  challengesWithoutPlayoffs.length = numberOfChallenges;

  const myChallenges = Object.assign(challengesWithoutPlayoffs, { season });
  if (isCurrentlyPlayoffs) Object.assign(myChallenges, { postseason });

  const MiniBanner = (props) => {
    let title;
    let date;
    let statusColor;
    let statusText;
    let text;
    let img;

    // remove monthlyBannerDate update if description won't follow challenges/date standard

    Object.keys(myChallenges).forEach((challengeItem) => {
      if (myChallenges[challengeItem].status === 'current') {
        monthlyBannerDate = myChallenges[challengeItem].description;
      }
      return monthlyBannerDate;
    });

    switch (props.variant) {
      case 'playoffs':
        title = playoffsBannerTitle;
        date = playoffsBannerDate;
        statusColor = isCurrentlyPlayoffs ? 'g600' : 'n850';
        statusText = isCurrentlyPlayoffs ? statusInProgressText : statusCompleteText;
        text = playoffsBannerText;
        img = playoffsBannerIMG;
        break;
      case 'monthly':
        title = monthlyBannerTitle;
        date = isFrench ? convertToFrenchDateRange(monthlyBannerDate) : monthlyBannerDate;
        statusColor = isCurrentlySeason ? 'g600' : 'n850';
        statusText = isCurrentlySeason ? statusInProgressText : statusCompleteText;
        text = monthlyBannerText;
        img = monthlyBannerIMG;
        break;
      case 'season':
        title = seasonBannerTitle;
        date = seasonBannerDate;
        statusColor = isCurrentlySeason ? 'g600' : 'n850';
        statusText = isCurrentlySeason ? statusInProgressText : statusCompleteText;
        text = seasonBannerText;
        img = seasonBannerIMG;
        break;
    }

    return (
      <MiniBannerContainer>
        <MiniBannerInformationContainer>
          <MiniBannerTitle as='h5' noMarginTop noMarginBottom>
            {title}
          </MiniBannerTitle>
          <MiniBannerStatus>
            <MiniBannerDate>{date}</MiniBannerDate>
            <MiniBannerBadge color={statusColor}>{statusText}</MiniBannerBadge>
          </MiniBannerStatus>
          <MiniBannerText>{text}</MiniBannerText>
          <MiniBannerLink onClick={() => dispatch(setHeatStreakPanelToClosed())} to={miniBannerLinkRoute}>
            {miniBannerLinkText}
          </MiniBannerLink>
        </MiniBannerInformationContainer>
        <MiniBannerImage src={`${cdnUrl}${img}`} />
      </MiniBannerContainer>
    );
  };

  const getSeasonOrPostseasonProperty = (challengeItem, property) => {
    const properties = {
      color: {
        postseason: isCurrentlyPlayoffs ? theme.colors.g900 : theme.colors.n850,
        season: isCurrentlySeason ? theme.colors.g900 : theme.colors.n850,
      },
      rank: { postseason: postseason.rank || '--', season: season.rank || '--' },
      title: { postseason: postseasonStatsTitle, season: seasonTitle },
      dates: { postseason: playoffDates, season: seasonDates },
      status: {
        postseason: isCurrentlyPlayoffs ? statusInProgressText.toUpperCase() : statusCompleteText.toUpperCase(),
        season: isCurrentlySeason ? statusInProgressText.toUpperCase() : statusCompleteText.toUpperCase(),
      },
      rankRatio: {
        postseason: `${postseason.rank || '--'}/${postseason.totalUsers || '--'}`,
        season: `${season.rank || '--'}/${season.totalUsers || '--'}`,
      },
    };

    return properties[property]?.[challengeItem] || '--';
  };

  const renderProperty = (challengeItem, property) => {
    if (challengeItem === 'postseason' || challengeItem === 'season') {
      return getSeasonOrPostseasonProperty(challengeItem, property);
    } else {
      switch (property) {
        case 'color':
          return myChallenges[challengeItem].status === 'current' ? theme.colors.g900 : theme.colors.n850;
        case 'rank':
          return myChallenges[challengeItem].rank || '--';
        case 'title':
          return `${challengeMonthlyTitle} ${myChallenges[challengeItem].challengeNumber}`;
        case 'dates':
          return challengeDescriptions[challengeItem];
        case 'status':
          return myChallenges[challengeItem].status === 'current'
            ? statusInProgressText.toUpperCase()
            : statusCompleteText.toUpperCase();
        case 'rankRatio':
          return `${myChallenges[challengeItem].rank || '--'}/${myChallenges[challengeItem].totalUsers || '--'}`;
        default:
          return '--';
      }
    }
  };

  return (
    <Container>
      <Spacing sizeLeft='mega' sizeRight='mega' sizeBottom='giga'>
        {currentChallenge && (
          <Spacing sizeBottom='mega'>
            <Carousel data={carouselData} />
          </Spacing>
        )}
        <SubContainer>
          <Spacing sizeBottom='mega'>
            <ColorProvider variant='white'>
              <StyledHeading as='h3' size='mega' noMarginBoth>
                {winStreakHeader}
              </StyledHeading>
            </ColorProvider>
          </Spacing>
          <Spacing sizeBottom='mega'>
            <WinStreak>
              <WinStreakGraph>
                <HeatStreakGraph
                  showIcon
                  assetSrc='https://cdn.rbi.digital/hockey/icon_flame_large.png'
                  gradientId='heatStreakChart'
                  graphVariant={heatStreakVariant}
                  variant='image'
                  percentage={heatStreakPercentage}
                />
              </WinStreakGraph>
              <WinStreakText>
                <ColorProvider variant='white'>
                  <StyledHeading as='h3' size='mega' noMarginBoth>
                    {winStreakTitle}
                  </StyledHeading>
                </ColorProvider>
                <ColorProvider variant='n800'>
                  <StyledText size='giga' noMargin>
                    {winStreakText}
                  </StyledText>
                </ColorProvider>
              </WinStreakText>
            </WinStreak>
          </Spacing>

          <Spacing>
            <StreakDates>
              {streakList.map(({ ...props }, index) => (
                <HeatStreakItem key={`streak-${index}`}>
                  <HeatStreakGraph
                    gradientId={`h${index}-streak-chart`}
                    percentage='100'
                    text={streakBadge}
                    secondaryText={index + 1}
                    size='bit'
                    isFrench={isFrench}
                    {...props}
                  />
                </HeatStreakItem>
              ))}
            </StreakDates>
          </Spacing>
        </SubContainer>

        {myChallenges && (
          <SubContainer>
            <Spacing sizeBottom='mega'>
              <ColorProvider variant='white'>
                <StyledHeading as='h3' size='mega' noMarginBoth>
                  {myChallengesTitle}
                </StyledHeading>
              </ColorProvider>
            </Spacing>

            {Object.keys(myChallenges)
              .reverse()
              .map((challengeItem, index) => {
                return (
                  <div key={challengeItem}>
                    <Grid style={{ paddingTop: '10px', paddingBottom: '10px', alignItems: 'flex-start' }}>
                      <Col columns='8' style={{ display: 'flex', alignItems: 'flex-start' }}>
                        <ColorProvider variant='white' style={{ paddingRight: '10px' }}>
                          <CircleText color={renderProperty(challengeItem, 'color')} size='byte' width='35px' bold>
                            {renderProperty(challengeItem, 'rank')}
                          </CircleText>
                        </ColorProvider>
                        <ColorProvider variant='white'>
                          <Text size='giga' bold>
                            {renderProperty(challengeItem, 'title')}
                          </Text>
                          <ColorProvider variant='n550'>
                            <Text size='giga'>{renderProperty(challengeItem, 'dates')}</Text>
                          </ColorProvider>
                        </ColorProvider>
                      </Col>
                      <Col columns='3.5'>
                        <Spacing>
                          <ColorProvider variant='white' style={{ textAlign: 'right' }}>
                            <StatusBadge color={renderProperty(challengeItem, 'color')} size='byte' bold>
                              {renderProperty(challengeItem, 'status')}
                            </StatusBadge>
                            <Text size='giga'>{renderProperty(challengeItem, 'rankRatio')}</Text>
                          </ColorProvider>
                        </Spacing>
                      </Col>
                    </Grid>
                    {index < Object.keys(myChallenges).length && <Divider />}
                  </div>
                );
              })}
          </SubContainer>
        )}
        <Spacing sizeBottom='mega'>
          {isCurrentlyPlayoffs && <MiniBanner variant='playoffs' />}
          {!isCurrentlyPlayoffs && <MiniBanner variant='season' />}
        </Spacing>
        <Spacing sizeBottom='mega'>{!isCurrentlyPlayoffs && <MiniBanner variant='monthly' />}</Spacing>
        <UpdateUserName />
      </Spacing>
    </Container>
  );
};

export default HeatStreakPanel;
